import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, ShieldCheck, Search, Loader2, Gift, Undo2, Trash2, UserX, Clock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only panel that lists users and lets the admin grant or revoke
// a subscription waiver. Talks to:
//   GET  /api/admin/users
//   POST /api/admin/users/waiver  { user_id, waived, waived_until? }
const STATUS_TONE = {
  active:    { bg: "#E8F0E7", fg: "#3090A8", label: "Active" },
  trialing:  { bg: "#EAF0F9", fg: "#1B2349", label: "Trialing" },
  waived:    { bg: "#FEF3E7", fg: "#B07B3A", label: "Waived" },
  expired:   { bg: "#FEF2F0", fg: "#D98880", label: "Expired" },
};

const StatusPill = ({ status }) => {
  const t = STATUS_TONE[status] || STATUS_TONE.expired;
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: t.bg, color: t.fg }}
      data-testid={`waiver-status-${status}`}
    >
      {t.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

// Tight "3d ago / Mar 4" relative helper for the activity column.
// Returns null for nulls so callers can render "—" themselves.
const formatRelative = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const delta = (Date.now() - t) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  if (delta < 86400 * 7) return `${Math.floor(delta / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const UserWaiverPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // userId currently being mutated → show inline spinner
  const [busyId, setBusyId] = useState(null);
  // Per-user expiry input (ISO date string, or "" for permanent)
  const [expiryDraft, setExpiryDraft] = useState({});
  // {kind: 'practice' | 'account', user: {...}} | null
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data || []);
    } catch (e) {
      toast.error("Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const grant = async (u) => {
    setBusyId(u.user_id);
    try {
      const draft = expiryDraft[u.user_id];
      const body = { user_id: u.user_id, waived: true };
      if (draft) {
        // Convert YYYY-MM-DD to end-of-day UTC ISO
        body.waived_until = new Date(`${draft}T23:59:59Z`).toISOString();
      }
      await axios.post(`${API}/admin/users/waiver`, body, { withCredentials: true });
      toast.success(draft ? `Waiver granted until ${draft}.` : "Permanent waiver granted.");
      await refresh();
    } catch (e) {
      toast.error("Could not grant waiver.");
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Revoke waiver for ${u.email}?`)) return;
    setBusyId(u.user_id);
    try {
      await axios.post(
        `${API}/admin/users/waiver`,
        { user_id: u.user_id, waived: false },
        { withCredentials: true }
      );
      toast.success("Waiver revoked.");
      await refresh();
    } catch (e) {
      toast.error("Could not revoke waiver.");
    } finally {
      setBusyId(null);
    }
  };

  // Force-expire a user's trial so their next sign-in surfaces the paywall.
  // Used to provision Apple's App Review reviewer accounts that need to
  // exercise the IAP purchase flow.
  const forceExpire = async (u) => {
    if (!window.confirm(
      `Force-expire trial for ${u.email}?\n\n` +
      "Their subscription status will be set to 'expired' and their next " +
      "sign-in will surface the paywall (Pricing screen). This is the state " +
      "Apple's App Review needs to test the purchase flow."
    )) return;
    setBusyId(u.user_id);
    try {
      await axios.post(
        `${API}/admin/users/force-expire`,
        { user_id: u.user_id },
        { withCredentials: true }
      );
      toast.success(`${u.email} is now marked expired.`);
      await refresh();
    } catch (e) {
      toast.error("Could not force-expire trial.");
    } finally {
      setBusyId(null);
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    const { kind, user: u } = confirmDelete;
    setBusyId(u.user_id);
    try {
      const url =
        kind === "practice"
          ? `${API}/admin/users/${u.user_id}/practice-data`
          : `${API}/admin/users/${u.user_id}`;
      const res = await axios.delete(url, { withCredentials: true });
      const total = Object.values(res.data?.deleted || {}).reduce(
        (sum, n) => sum + (Number(n) || 0),
        0
      );
      if (kind === "practice") {
        toast.success(`Wiped ${total} practice records for ${u.email}.`);
      } else {
        toast.success(`Account for ${u.email} deleted.`);
      }
      setConfirmDelete(null);
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || "Delete failed.";
      toast.error(String(detail));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = users.filter((u) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div
      className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-8"
      data-testid="user-waiver-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#3090A8]" />
            Subscription Waivers
          </h2>
          <p className="text-sm text-[#718096]">
            Grant comp access to a user permanently or until a specific date.
            Revoke anytime — the user falls back to their existing plan state.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email or name…"
            className="pl-9 rounded-xl text-sm"
            data-testid="waiver-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex items-center justify-center text-[#718096]">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading users…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-[#718096] text-sm">No users match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="waiver-users-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-3 text-[#718096] font-medium">User</th>
                <th className="text-left py-3 px-3 text-[#718096] font-medium">Status</th>
                <th className="text-left py-3 px-3 text-[#718096] font-medium">Expires</th>
                <th className="text-left py-3 px-3 text-[#718096] font-medium">Activity</th>
                <th className="text-left py-3 px-3 text-[#718096] font-medium">Waiver Action</th>
                <th className="text-left py-3 px-3 text-[#718096] font-medium">Data</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isWaived = u.subscription_status === "waived";
                const draft = expiryDraft[u.user_id] ?? "";
                return (
                  <tr key={u.user_id} className="border-b border-slate-50 align-top" data-testid={`waiver-row-${u.user_id}`}>
                    <td className="py-3 px-3">
                      <div className="font-medium text-[#1B2349]">{u.name || "—"}</div>
                      <div className="text-xs text-[#718096]">{u.email}</div>
                    </td>
                    <td className="py-3 px-3">
                      <StatusPill status={u.effective_status || u.subscription_status || "expired"} />
                    </td>
                    <td className="py-3 px-3 text-[#4A5568]">
                      {isWaived
                        ? (u.waived_until ? formatDate(u.waived_until) : "Permanent")
                        : formatDate(u.effective_expires_at)}
                    </td>
                    <td className="py-3 px-3" data-testid={`activity-${u.user_id}`}>
                      <div className="flex flex-col gap-0.5 text-xs leading-tight">
                        <span className="text-[#1B2349]">
                          <span className="font-semibold">{u.practice_count ?? 0}</span>
                          <span className="text-[#718096]"> practices</span>
                        </span>
                        <span className="text-[#718096]">
                          Last seen: {formatRelative(u.last_seen_at) || "—"}
                        </span>
                        <span className="text-[#718096]">
                          Last practice: {formatRelative(u.last_practice_at) || "never"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="date"
                          value={draft}
                          onChange={(e) =>
                            setExpiryDraft((d) => ({ ...d, [u.user_id]: e.target.value }))
                          }
                          className="h-9 rounded-lg border border-slate-200 px-2 text-xs text-[#1B2349] focus:outline-none focus:ring-2 focus:ring-[#78C0D8]/40"
                          data-testid={`waiver-expiry-${u.user_id}`}
                          aria-label="Waiver expiry (leave blank for permanent)"
                        />
                        <Button
                          size="sm"
                          onClick={() => grant(u)}
                          disabled={busyId === u.user_id}
                          className="rounded-full bg-[#3090A8] hover:bg-[#26788C] text-white px-3 h-9 text-xs"
                          data-testid={`waiver-grant-${u.user_id}`}
                        >
                          {busyId === u.user_id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Gift className="w-3.5 h-3.5 mr-1.5" />}
                          {isWaived ? "Update" : "Grant"}
                        </Button>
                        {isWaived && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revoke(u)}
                            disabled={busyId === u.user_id}
                            className="rounded-full border-[#D98880] text-[#D98880] hover:bg-[#FEF2F0] px-3 h-9 text-xs"
                            data-testid={`waiver-revoke-${u.user_id}`}
                          >
                            <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                            Revoke
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-[#718096] mt-1.5 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Empty date = permanent waiver
                      </p>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex flex-col gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => forceExpire(u)}
                          disabled={busyId === u.user_id || u.subscription_status === "expired"}
                          className="rounded-full border-[#B07B3A] text-[#B07B3A] hover:bg-[#FEF3E7] px-3 h-9 text-xs"
                          data-testid={`force-expire-${u.user_id}`}
                        >
                          <Clock className="w-3.5 h-3.5 mr-1.5" />
                          {u.subscription_status === "expired" ? "Already expired" : "Force expire trial"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDelete({ kind: "practice", user: u })}
                          disabled={busyId === u.user_id}
                          className="rounded-full border-slate-300 text-[#4A5568] hover:bg-slate-50 px-3 h-9 text-xs"
                          data-testid={`delete-practice-${u.user_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Clear Practice
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDelete({ kind: "account", user: u })}
                          disabled={busyId === u.user_id}
                          className="rounded-full border-[#D98880] text-[#D98880] hover:bg-[#FEF2F0] px-3 h-9 text-xs"
                          data-testid={`delete-account-${u.user_id}`}
                        >
                          <UserX className="w-3.5 h-3.5 mr-1.5" />
                          Delete Account
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}
      >
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.kind === "practice"
                ? "Clear practice data?"
                : "Delete user account?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.kind === "practice" ? (
                <>
                  This wipes <strong>{confirmDelete?.user?.email}</strong>'s
                  practice history, progress, favorites, reminders, and "not
                  improving" events. Their account and subscription stay
                  intact. This cannot be undone.
                </>
              ) : (
                <>
                  This permanently deletes <strong>{confirmDelete?.user?.email}</strong>'s
                  account and all associated data. Payment records are
                  anonymised for legal retention. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-[#D98880] hover:bg-[#C0726B] text-white"
              data-testid="admin-delete-confirm"
            >
              {confirmDelete?.kind === "practice" ? "Clear data" : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserWaiverPanel;
